/* eslint react/require-default-props: ["error", { "functions": "defaultArguments" }] */
import React from 'react';
import styled from '@emotion/styled';
import { colors, heavy, book, regular, semiBold } from '@/styles';
import { Button, ButtonProps } from '@/atoms';
import { LivefireNoTracking } from '@/svgs';
import { StyledComponent } from '@/helpers';

export type ImageEmptyStateProps = StyledComponent & {
  children?: React.ReactNode | React.ReactNode[];
  title?: string;
  description?: string;
  buttonText?: string;
  onButtonClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
  showAction?: boolean;
  isRebrand?: boolean;
  buttonColor?: ButtonProps['color'];
};

const defaultDescription =
  'Simply record and send your first video and tracking \ninformation will populate here.';

/**
 * ImageEmptyState component
 */
function ImageEmptyState({
  className,
  children = null,
  title = 'No tracking yet!',
  description = defaultDescription,
  buttonText = 'Record a Video',
  onButtonClick = null,
  showAction = true,
  isRebrand = false,
  buttonColor = 'traffic-cone',
}: ImageEmptyStateProps) {
  return (
    <div className={className}>
      <div className="children">{children || <LivefireNoTracking />}</div>
      <div className="details">
        <h1 className="title">{title}</h1>
        <p>
          {description.split('\n').map((text) => (
            <React.Fragment key={text}>
              {text}
              <br />
            </React.Fragment>
          ))}
        </p>
        {onButtonClick && showAction && (
          <Button
            onClick={onButtonClick}
            color={buttonColor}
            label={buttonText}
            rectangle
            isRebrand={isRebrand}
          />
        )}
      </div>
    </div>
  );
}

const styledImageEmptyState = styled(ImageEmptyState)`
  ${({ isRebrand = false }) => (isRebrand ? regular() : book())}
  background: ${colors.white};
  padding: 3.875rem 0;

  /* the children container has no padding to allow it to maintain full width */
  .children {
    padding: 0;
  }

  /* details has some side padding */
  .details {
    padding: 0 1.5625rem;
  }

  h1.title {
    /* .title for specificity */
    ${({ isRebrand = false }) => (isRebrand ? semiBold() : heavy())}
    color: ${colors.silverSandAlt};
    padding-bottom: 0.64875rem;
    margin: 0;
    font-size: 1.125rem;
  }

  p {
    ${({ isRebrand = false }) => (isRebrand ? regular() : book())}
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: ${colors.silverSandAlt};
    padding: 0 0 1.6875rem;
    margin: 0;
  }
`;

export default styledImageEmptyState;
